import { create } from 'zustand'
import type { TourAccessContext, TourActivator, TourEndEvent, TourSource } from '../types'

export type { TourEndEvent, TourSource }

interface WalkthroughState {
  /** A start request for the host to pick up; `nonce` lets the same tour be restarted. */
  request: { tourId: string; source: TourSource; nonce: number } | null
  /** Tour currently on screen (null when idle). */
  activeTourId: string | null
  /** Sidebar groups, published by the host so eligibility hooks outside it agree. */
  navGroups: TourAccessContext['navGroups']
  /** Start (or restart) a tour by id — callable from anywhere, not just the menu. */
  startTour: (tourId: string, source?: TourSource) => void
  setActiveTourId: (id: string | null) => void
  /** The host clears a request once handled, so a remount never replays it. */
  consumeRequest: () => void
  setNavGroups: (groups: TourAccessContext['navGroups']) => void
  /** Page tour whose ? beacon is lit (progressive onboarding), or null. */
  beaconTourId: string | null
  setBeaconTourId: (id: string | null) => void
}

let nonce = 0

export const useWalkthroughStore = create<WalkthroughState>((set) => ({
  request: null,
  activeTourId: null,
  navGroups: [],
  startTour: (tourId, source = 'api') => set({ request: { tourId, source, nonce: ++nonce } }),
  setActiveTourId: (activeTourId) => set({ activeTourId }),
  consumeRequest: () => set({ request: null }),
  setNavGroups: (navGroups) => set({ navGroups }),
  beaconTourId: null,
  setBeaconTourId: (beaconTourId) => set({ beaconTourId }),
}))

const endListeners = new Set<(e: TourEndEvent) => void>()

/** Subscribe to tour ends. Returns the unsubscribe function. */
export function onTourEnd(listener: (e: TourEndEvent) => void): () => void {
  endListeners.add(listener)
  return () => {
    endListeners.delete(listener)
  }
}

export function emitTourEnd(e: TourEndEvent): void {
  for (const listener of [...endListeners]) listener(e)
}

// ── Page-owned view activators (see TourStep.activate) ──

const activators = new Map<string, TourActivator>()

export function registerTourActivator(name: string, activator: TourActivator): () => void {
  activators.set(name, activator)
  return () => {
    if (activators.get(name) === activator) activators.delete(name)
  }
}

export function getTourActivator(name: string): TourActivator | undefined {
  return activators.get(name)
}
